import React, { useState, FormEvent } from 'react';
import { Role, ROLE_OPTIONS } from '../types/member-roles';

export interface AddUserModalProps {
  onSubmit: (data: { fullName: string; email: string; role: Role; password: string }) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
  },
  content: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '28px', width: '420px', maxWidth: '90vw',
    maxHeight: '90vh', overflowY: 'auto',
  },
  title: { fontSize: '18px', fontWeight: 700, color: '#1a6b3c', marginBottom: '6px' },
  subtitle: { fontSize: '13px', color: '#6b7280', marginBottom: '20px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '8px 12px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  hint: { fontSize: '12px', color: '#888', marginTop: '4px' },
  hintError: { fontSize: '12px', color: '#d32f2f', marginTop: '4px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '16px' },
  submitBtn: { padding: '10px 20px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  submitBtnDisabled: { padding: '10px 20px', backgroundColor: '#a5d6a7', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'not-allowed' },
  cancelBtn: { padding: '10px 20px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Standalone "Add User" modal — creates a KDU Hub account from scratch
 * (no Member record and no Legacy Ball ticket required). Calls POST /users.
 */
export function AddUserModal({ onSubmit, onClose, loading }: AddUserModalProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('Attendee');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const nameValid = fullName.trim().length > 0;
  const emailValid = EMAIL_RE.test(email.trim());
  const passwordValid = password.length >= 8;
  const canSubmit = nameValid && emailValid && passwordValid && !loading;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setError('');
    try {
      await onSubmit({ fullName: fullName.trim(), email: email.trim().toLowerCase(), role, password });
    } catch (err: any) {
      setError(err?.message || 'Failed to create user');
    }
  }

  return (
    <div style={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="add-user-modal-title">
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        <h3 id="add-user-modal-title" style={styles.title}>Add User</h3>
        <p style={styles.subtitle}>Create a hub account directly — no Member record or Legacy Ball ticket needed.</p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="add-user-name">Full Name</label>
            <input
              id="add-user-name"
              style={styles.input}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Jane Doe"
              disabled={loading}
            />
            {touched && !nameValid && <p style={styles.hintError}>Full name is required</p>}
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="add-user-email">Email</label>
            <input
              id="add-user-email"
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              disabled={loading}
              autoComplete="off"
            />
            {touched && !emailValid && <p style={styles.hintError}>Enter a valid email address</p>}
            <p style={styles.hint}>This becomes their login username.</p>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="add-user-role">Role</label>
            <select
              id="add-user-role"
              style={styles.select}
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={loading}
            >
              {ROLE_OPTIONS.filter((opt) => opt.value !== 'Admin').map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="add-user-password">Password</label>
            <input
              id="add-user-password"
              style={styles.input}
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
              disabled={loading}
              autoComplete="off"
            />
            {touched && !passwordValid
              ? <p style={styles.hintError}>Password must be at least 8 characters</p>
              : <p style={styles.hint}>Share this with the user; they can change it later.</p>}
          </div>

          <div style={styles.btnRow}>
            <button type="submit" style={canSubmit ? styles.submitBtn : styles.submitBtnDisabled} disabled={!canSubmit}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
            <button type="button" style={styles.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
